/**
 * Navigation contributed by the AUTH layer.
 *
 * ── Why a registry rather than one list ───────────────────────────────────
 * Every layer that adds signed-in pages also needs to add sidebar entries for
 * them. The obvious approach — one NAV array in the shell — means each new
 * layer must edit the shell, and a layer cannot edit a file it does not own
 * without shipping a whole copy of it. That is how this codebase briefly ended
 * up with two 340-line copies of the same shell kept in sync by hand.
 *
 * Instead each layer drops a `nav.<layer>.ts` here exporting its own items.
 * AppShell globs them at BUILD time and merges. Adding a layer is adding one
 * small file; the shell is never touched and cannot drift.
 */
import type { LucideIcon } from 'lucide-react';

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Exact-match the route. Needed for index routes like /dashboard. */
  end?: boolean;
  /** Hide from anyone without this role. Presentation only — see AppShell. */
  role?: string;
  /** A firm role this item needs, and up. Presentation — the API refuses too. */
  minRole?: 'paralegal' | 'attorney' | 'owner';
  /** Hide from console team members; owners only. Presentation — the server refuses them too. */
  ownerOnly?: boolean;
  /** Starts a labelled group above this item. */
  section?: string;
  /** Ascending. Leave gaps so a later layer can slot between without renumbering. */
  order?: number;
};

export const items: NavItem[] = [
  /*
   * ── Where "Ask" went ──────────────────────────────────────────────────
   * It used to sit here, third in the Work group. It is now in the top bar
   * beside the theme control (see DashboardLayoutRoute), because it is not
   * the same kind of thing as the items around it: Overview, Pipeline and
   * Matters are PLACES you go and stay, and the sidebar marks which one you
   * are in. Ask is something you do about the place you are already in —
   * mid-task, on the screen you are looking at — and giving it a sidebar
   * slot meant leaving that screen to use it, then finding your way back.
   *
   * The route is still /dashboard: the template owns that path and renaming
   * it would break every existing link.
   */

  /* Profile is deliberately NOT here. It is reachable from the avatar menu,
   * which is where people look for their own account, and a sidebar slot
   * spent on it is a slot not spent on the work. */
];
